import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PencilSimple, Trash, ArrowRight, HardHat, UsersThree, Signature as SigIcon, SkipForward } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import ConfirmDialog from "@/components/ConfirmDialog";
import InterventionTypeToggle from "@/components/InterventionTypeToggle";
import InterventionCategoryPicker from "@/components/InterventionCategoryPicker";
import TimeRangeInput from "@/components/TimeRangeInput";
import AssigneesPicker from "@/components/AssigneesPicker";
import ColorPickerField from "@/components/ColorPickerField";
import SignaturePad from "@/components/SignaturePad";
import AxonautQuotationPicker from "@/components/AxonautQuotationPicker";

/** Edit an existing intervention (time / duration / tech / title / status) + delete. */
export default function EditInterventionDialog({ open, onOpenChange, intervention, team, chantiersById, clientsById, onChanged }) {
  const { isAdmin } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [sigPromptOpen, setSigPromptOpen] = useState(false);
  const [sigPadOpen, setSigPadOpen] = useState(false);

  useEffect(() => {
    if (!intervention) return;
    setForm({
      scheduled_date: (intervention.scheduled_date || "").slice(0, 16),
      duration_min: intervention.duration_min ?? 60,
      technician_id: intervention.technician_id,
      assignee_ids: intervention.assignee_ids || [],
      color: intervention.color || null,
      title: intervention.title || "",
      type: intervention.type || "intervention",
      categorie: intervention.categorie || "electricite",
      status: intervention.status || "planned",
    });
  }, [intervention]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Persist the intervention update to the backend. Reused by both branches
  // of the closure signature flow.
  const doPatch = async () => {
    setSaving(true);
    try {
      await api.patch(`/interventions/${intervention.id}`, form);
      toast.success("Intervention mise à jour");
      onOpenChange(false);
      onChanged?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const save = () => {
    // Closure flow: if the tech is flipping status to "done" from something
    // else, prompt for a client signature first. Optional — they can skip.
    const isClosing = form.status === "done" && intervention?.status !== "done";
    if (isClosing && intervention?.chantier_id) {
      setSigPromptOpen(true);
      return;
    }
    doPatch();
  };

  const chantier = chantiersById && intervention?.chantier_id ? chantiersById[intervention.chantier_id] : null;
  const client = clientsById && chantier?.client_id ? clientsById[chantier.client_id] : null;
  const defaultClientName = client?.name || chantier?.client_name || "";

  const remove = async () => {
    setDeleting(true);
    try {
      await api.delete(`/interventions/${intervention.id}`);
      toast.success("Intervention supprimée");
      setConfirmDel(false);
      onOpenChange(false);
      onChanged?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setDeleting(false); }
  };

  // Link an Axonaut devis to the PARENT chantier from within the intervention
  // dialog — a devis is a property of the chantier, not of a single visit.
  const linkQuotation = async (id) => {
    if (!intervention?.chantier_id) return;
    try {
      await api.patch(`/chantiers/${intervention.chantier_id}`, { axonaut_quotation_id: id });
      toast.success(id ? "Devis Axonaut lié au chantier" : "Devis Axonaut délié");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (!intervention) return null;
  const techs = (team || []).filter((u) => u.is_active !== false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-[#111] border-white/10 text-white rounded-2xl p-0 max-h-[90vh] flex flex-col gap-0" data-testid="edit-iv-dialog">
        <DialogHeader className="space-y-0.5 p-4 pb-2 shrink-0">
          <DialogTitle className="font-display text-base flex items-center gap-2">
            <PencilSimple size={16} className="text-[#005CFF]" weight="duotone" /> Modifier l&apos;intervention
          </DialogTitle>
          <DialogDescription className="text-[10px] uppercase tracking-widest text-white/50">
            {intervention.title}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-4 pb-2 min-h-0">
        {intervention.chantier_id && (
          <button
            onClick={() => { onOpenChange(false); nav(`/chantiers/${intervention.chantier_id}`); }}
            className="w-full rounded-xl bg-gradient-to-r from-[#005CFF]/25 to-[#005CFF]/10 border border-[#005CFF]/40 hover:border-[#005CFF] px-2.5 py-2 flex items-center gap-2.5 transition-colors group"
            data-testid="edit-iv-open-chantier"
          >
            <div className="w-7 h-7 rounded-md bg-[#005CFF]/25 flex items-center justify-center shrink-0">
              <HardHat size={14} weight="duotone" className="text-[#7CB2FF]" />
            </div>
            <div className="flex-1 text-left min-w-0">
              <div className="text-[8px] uppercase tracking-widest text-[#7CB2FF] font-semibold leading-tight">Fiche chantier</div>
              <div className="font-display font-semibold text-xs truncate leading-tight">
                {chantier?.name || "Ouvrir le chantier lié"}
              </div>
              {chantier?.address && (
                <div className="text-[9px] text-white/50 truncate leading-tight">{chantier.address}</div>
              )}
            </div>
            <ArrowRight size={14} weight="bold" className="text-[#7CB2FF] shrink-0 group-hover:translate-x-0.5 transition-transform" />
          </button>
        )}

        <div className="space-y-2 mt-2">
          <InterventionTypeToggle value={form.type} onChange={(v) => set("type", v)} testid="edit-iv-type" />
          <div className="rounded-xl bg-white/[0.02] border border-white/10 p-2 space-y-1.5">
            <div className="text-[9px] uppercase tracking-widest text-white/50 px-0.5">
              Corps de métier
            </div>
            <InterventionCategoryPicker
              value={form.categorie}
              onChange={(v) => set("categorie", v)}
              testid="edit-iv-categorie"
            />
          </div>

          <Field label="Titre *">
            <Input value={form.title || ""} onChange={(e) => set("title", e.target.value)} className="bg-white/5 border-white/10 h-9 text-sm" data-testid="edit-iv-title" />
          </Field>
          {isAdmin && (
            <Field label="Technicien principal">
              <Select value={form.technician_id || ""} onValueChange={(v) => set("technician_id", v)}>
                <SelectTrigger className="bg-white/5 border-white/10 h-9 text-sm" data-testid="edit-iv-tech"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                <SelectContent className="bg-[#111] border-white/10 text-white">
                  {techs.map((u) => (<SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
          )}
          {isAdmin && (
            <div className="rounded-xl bg-white/[0.02] border border-white/10 p-2 space-y-1.5">
              <div className="flex items-center gap-1 text-white/60 text-[9px] uppercase tracking-widest">
                <UsersThree size={10} weight="duotone" /> Co-affectés
              </div>
              <AssigneesPicker
                team={techs.filter((u) => u.id !== form.technician_id)}
                value={form.assignee_ids || []}
                onChange={(ids) => set("assignee_ids", ids)}
                testid="edit-iv-assignees"
              />
              <div className="text-[9px] text-white/40 leading-tight">Ajoute autant de co-affectés que nécessaire.</div>
            </div>
          )}
          <TimeRangeInput
            scheduled_date={form.scheduled_date}
            duration_min={form.duration_min}
            onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
            testid="edit-iv-time"
          />
          <ColorPickerField
            value={form.color}
            onChange={(v) => set("color", v)}
            testid="edit-iv-color"
            label="Couleur"
          />
          <Field label="Statut">
            <Select value={form.status || "planned"} onValueChange={(v) => set("status", v)}>
              <SelectTrigger className="bg-white/5 border-white/10 h-9 text-sm" data-testid="edit-iv-status"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#111] border-white/10 text-white">
                <SelectItem value="planned">Planifiée</SelectItem>
                <SelectItem value="in_progress">En cours</SelectItem>
                <SelectItem value="done">Terminée</SelectItem>
                <SelectItem value="cancelled">Annulée</SelectItem>
              </SelectContent>
            </Select>
          </Field>

          {isAdmin && intervention?.chantier_id && (
            <AxonautQuotationPicker
              value={chantier?.axonaut_quotation_id}
              onChange={linkQuotation}
              clientId={chantier?.client_id}
              testidPrefix="edit-iv-axo"
            />
          )}
        </div>
        </div>

        <div className="flex gap-2 p-3 border-t border-white/10 bg-[#111] shrink-0 rounded-b-2xl" data-testid="edit-iv-footer">
          {isAdmin && (
            <Button variant="ghost" onClick={() => setConfirmDel(true)} className="border border-red-500/30 text-red-400 hover:bg-red-500/10 h-10 px-3" data-testid="edit-iv-delete">
              <Trash size={14} />
            </Button>
          )}
          <div className="flex-1" />
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="border border-white/10 hover:bg-white/5 h-10 text-sm">Annuler</Button>
          <Button onClick={save} disabled={saving} className="bg-[#005CFF] hover:bg-[#0047CC] h-10 text-sm" data-testid="edit-iv-save">
            {saving ? "…" : "Enregistrer"}
          </Button>
        </div>

        <ConfirmDialog
          open={confirmDel}
          onOpenChange={setConfirmDel}
          onConfirm={remove}
          loading={deleting}
          title="Supprimer cette intervention ?"
          description={`« ${intervention.title || "Sans titre"} » — l'événement sera retiré de l'agenda du technicien. Irréversible.`}
          confirmLabel="Supprimer"
          testid="edit-iv-delete-confirm"
        />

        {/* Closure flow — ask the tech whether to collect a client signature */}
        <Dialog open={sigPromptOpen} onOpenChange={setSigPromptOpen}>
          <DialogContent className="max-w-sm bg-[#111] border-white/10 text-white rounded-2xl" data-testid="closure-sig-prompt">
            <DialogHeader>
              <DialogTitle className="font-display text-lg flex items-center gap-2">
                <SigIcon size={20} weight="duotone" className="text-[#005CFF]" />
                Clôture de l&apos;intervention
              </DialogTitle>
              <DialogDescription className="text-white/70 text-sm mt-1">
                Faire signer le client maintenant ? La signature sera intégrée automatiquement au rapport PDF.
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-2 mt-3">
              <Button
                onClick={() => { setSigPromptOpen(false); setSigPadOpen(true); }}
                className="bg-[#005CFF] hover:bg-[#0047CC] h-12 rounded-xl"
                data-testid="closure-sig-collect"
              >
                <SigIcon size={16} weight="fill" /> Oui, faire signer
              </Button>
              <Button
                variant="ghost"
                onClick={() => { setSigPromptOpen(false); doPatch(); }}
                disabled={saving}
                className="border border-white/10 hover:bg-white/5 h-12 rounded-xl text-white/80"
                data-testid="closure-sig-skip"
              >
                <SkipForward size={16} /> Passer sans signature
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <SignaturePad
          open={sigPadOpen}
          onClose={() => setSigPadOpen(false)}
          chantierId={intervention.chantier_id}
          interventionId={intervention.id}
          defaultClientName={defaultClientName}
          lockSignerType="client"
          hideTypeToggle
          onSaved={() => { setSigPadOpen(false); doPatch(); }}
        />
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-white/60 text-[9px] uppercase tracking-widest">{label}</Label>
      {children}
    </div>
  );
}
